'use client';

/**
 * SectionRouter — schema-driven dispatcher for rail brief cards.
 *
 * Reads a `SectionConfig` + entity record and renders the right
 * mode4-native section primitive. Mirrors the legacy
 * `<SectionRenderer />` under copilot/details/section-renderer.tsx
 * but dispatches to rail-native primitives so the rail brief
 * survives the CopilotPanel retirement.
 *
 * Field extraction supports dot-paths (`images.gallery_urls`) and
 * fallback fields. Section types not yet mode4-native render null
 * (graceful degradation — the legacy schema may declare sections
 * mode4 hasn't built primitives for yet).
 *
 * @see klyde_edge/ui_config/entity_schema.py — SectionType enum
 * @see klyde_edge/ui_config/docs/right_rail_and_canvas_ux_direction.md
 *
 * @layer presentation
 * @arch_component_type dispatcher
 * @arch_domain copilot / rail-insights
 */

import type { SectionConfig, DateField, ProductDocument } from '@/components/copilot/details/sections/types';
import { GallerySection } from './gallery-section';
import { TextBlock } from './text-block';
import { TagCloud } from './tag-cloud';
import { StatsGrid } from './stats-grid';
import { ContactSection } from './contact-section';
import { ProductsSection } from './products-section';
import { CollectionsSection } from './collections-section';
import { ListSection } from './list-section';
import { MetadataSection } from './metadata-section';
import { DocumentsSection } from './documents-section';

interface SectionRouterProps {
  config: SectionConfig;
  data: Record<string, unknown>;
  entityType: string;
  entityId: string;
}

/** Dot-path lookup: getValue(data, "images.primary"). */
function getValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object' && key in (acc as Record<string, unknown>)) {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
}

/** Best-effort condition evaluator — same shape the legacy uses. */
function evalCondition(condition: string, data: Record<string, unknown>): boolean {
  try {
    return Boolean(new Function('data', `return ${condition}`)(data));
  } catch {
    return true;
  }
}

function fieldName(f: string | DateField): string {
  return typeof f === 'string' ? f : f.field;
}

// Stat-tile value formatting: thousands-separated, and money fields carry the
// currency symbol so the Brief matches the canvas tiles ($4,980). Non-money
// stats (counts) get the separator only. (Bare $ is a known AUD/USD ambiguity
// to resolve later — currency-code on the response, not here.)
const MONEY_FIELD = /price|cost|amount|revenue|total|spend|quote/i;
function formatStatValue(field: string, v: unknown): string | number | null | undefined {
  if (typeof v !== 'number') return v as string | null | undefined;
  return MONEY_FIELD.test(field) ? `$${v.toLocaleString()}` : v.toLocaleString();
}

export function SectionRouter({ config, data, entityType, entityId }: SectionRouterProps) {
  if (config.condition && !evalCondition(config.condition, data)) return null;

  const value = config.field ? getValue(data, config.field) : null;
  const fallbackValue = config.fallback_field ? getValue(data, config.fallback_field) : null;
  const galleryValue = config.gallery_field ? getValue(data, config.gallery_field) : null;

  switch (config.type) {
    case 'image': {
      // Array.isArray([]) is true, so guard on length — an empty gallery
      // must fall through to the primary (field) / fallback_field, else a
      // product with only a primary image (e.g. a buyer wish: gallery_urls
      // empty, images.primary set) renders no image at all.
      const urls = Array.isArray(galleryValue) && galleryValue.length
        ? (galleryValue as string[])
        : typeof value === 'string' && value
        ? [value]
        : typeof fallbackValue === 'string' && fallbackValue
        ? [fallbackValue]
        : [];
      return (
        <GallerySection
          urls={urls}
          alt={(data.name as string | undefined) ?? entityType}
          aspectRatio={config.aspect_ratio ?? 'square'}
        />
      );
    }

    case 'stats': {
      const stats = (config.fields ?? []).map((f) => {
        const name = fieldName(f);
        const v = getValue(data, name);
        const label =
          typeof f === 'string'
            ? name.replace(/_/g, ' ')
            : f.label;
        return {
          label,
          value: formatStatValue(name, v),
        };
      });
      return <StatsGrid stats={stats} />;
    }

    case 'contact':
      return <ContactSection data={data} label={config.label} />;

    case 'products':
      return (
        <ProductsSection
          products={value as Parameters<typeof ProductsSection>[0]['products']}
          label={config.label}
          maxItems={config.max_items}
        />
      );

    case 'collections':
      return (
        <CollectionsSection
          collections={value as Parameters<typeof CollectionsSection>[0]['collections']}
          label={config.label}
          maxItems={config.max_items}
        />
      );

    case 'text':
      return <TextBlock text={value as string | null | undefined} label={config.label ?? 'Text'} />;

    case 'tags':
      return (
        <TagCloud
          data={data}
          fields={(config.fields ?? []).filter((f): f is string => typeof f === 'string')}
          label={config.label ?? 'Tags'}
        />
      );

    case 'list':
      return (
        <ListSection
          items={value as Parameters<typeof ListSection>[0]['items']}
          label={config.label}
          maxItems={config.max_items}
          entityType={entityType}
        />
      );

    case 'documents':
      return (
        <DocumentsSection
          items={value as ProductDocument[] | null | undefined}
          label={config.label ?? 'Documents'}
        />
      );

    case 'metadata':
      return <MetadataSection semanticId={entityId} />;

    // Section types we haven't built mode4-native primitives for yet
    // (status, dates, amount, party, items, responses, intent_lines,
    // axis_weights, cc_functions). Render null — the schema may
    // declare them but they belong to entity types mode4 doesn't
    // own. Adding a primitive = one more case here.
    default:
      return null;
  }
}
